/**
 * useCalendarWS — subscribe to topic 'calendar' and invalidate TanStack Query.
 *
 * v1.1 tech-debt closeout: the server has broadcast verb-only calendar events
 * since Phase 3 (apps/api/src/services/calendar.subscribers.ts, D-13) but no
 * client ever subscribed, so calendar/today views only refreshed on manual
 * refetch. This hook is the missing consumer — the mirror of useChoresWS.
 *
 * Server broadcasts (verb-only, no PII crosses the WS boundary — T-CAL-26):
 *   { topic:'calendar', event:'created' | 'updated' | 'deleted',
 *     data:{ id: seriesId, scope?: 'single'|'from_this'|'series' } }
 *
 * Every verb invalidates the ['calendar'] prefix, which covers both the
 * range query (['calendar','events']) and the dashboard/today query
 * (['calendar','today']). The cache stays authoritative — WS events never
 * patch row data directly (mirrors useChoresWS T-02-31 mitigation).
 *
 * Gjenoppkobling og backoff: se lib/reconnectingSocket.ts.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

export function useCalendarWS(): void {
  const qc = useQueryClient();

  useEffect(() => {
    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const msg = JSON.parse(String(data)) as {
          topic?: string;
          event?: string;
          data?: unknown;
        };
        if (msg?.topic !== 'calendar') return;
        switch (msg.event) {
          case 'created':
          case 'updated':
          case 'deleted':
            void qc.invalidateQueries({ queryKey: ['calendar'] });
            break;
          default:
            // Unknown verb — ignore silently (forward compatibility).
            break;
        }
      } catch {
        // Malformed frame — drop silently per Plan 1 P15 pattern.
      }
    };

    return openReconnectingSocket({ topics: ['calendar'], onMessage });
  }, [qc]);
}
